import { useState, useEffect } from 'react';
import {
  makeStyles,
  Typography,
  Button,
  Grid,
  FormControlLabel,
  TextField,
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import Modal from '../../Shared/Modal';
import AssessmentSelector
  from '../../PopulationHealth/widgets/ControlPanel/components/AssessmentSelector';
import ElegantSwitch from '../../Shared/ElegantSwitch';
import { enqueueSnackbar, SNACKBAR_VARIANTS } from '../../../redux_store';

const useStyles = makeStyles((theme) => ({
  input1: {
    height: 44,
  },
  modal: {
    width: '100%',
    maxWidth: 900,
    // height: '90vh',
    borderRadius: 16,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // Tablet adjustments
    [theme.breakpoints.down('md')]: {
      maxWidth: '95vw',
      height: '95vh',
      borderRadius: 12,
      margin: theme.spacing(1),
    },
    // Mobile adjustments
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100vw',
      height: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(4, 3),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    height: '100%',
    // Tablet adjustments
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(3, 2),
    },
    // Mobile adjustments
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 1.5),
    },
  },
  section: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3),
    background: '#ffffff',
    borderRadius: 12,
    border: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  },
  sectionDisabled: {
    background: '#f5f5f5',
    opacity: 0.7,
    '& .MuiFormLabel-root': {
      color: 'rgba(0, 0, 0, 0.38)',
    },
    '& .MuiInputBase-input': {
      color: 'rgba(0, 0, 0, 0.38)',
    },
    '& .MuiTypography-root': {
      color: 'rgba(0, 0, 0, 0.5)',
    },
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: theme.colors?.blue2 || '#122056',
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.colors?.lightBlue || '#5B65DC'}`,
  },
  actionsContainer: {
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    width: '100%',
    // Tablet adjustments
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2.5, 3),
      borderRadius: '0 0 12px 12px',
    },
    // Mobile adjustments
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      borderRadius: 0,
      flexDirection: 'column-reverse',
      gap: theme.spacing(1),
      '& > *': {
        width: '100%',
      },
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 10,
    padding: theme.spacing(1.5, 3),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
  },
  cancelButton: {
    color: theme.palette.text.secondary,
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  createButton: {
    backgroundColor: '#1976d2',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1565c0',
    },
  },
  switchControl: {
    '& .MuiTypography-body1': {
      fontSize: '0.9375rem',
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  },
  infoBox: {
    marginTop: theme.spacing(1),
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  radioGroup: {
    marginTop: theme.spacing(1),
  },
  radioOption: {
    border: '2px solid #e2e8f0',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#ffffff',
    '&:hover': {
      borderColor: '#bfdbfe',
      backgroundColor: '#f8fafc',
    },
    '&.selected': {
      borderColor: '#1976d2',
      backgroundColor: 'rgba(25, 118, 210, 0.04)',
      boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.1)',
    },
  },
  radioHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  radioCircle: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    border: '2px solid #d1d5db',
    marginRight: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    '&.selected': {
      borderColor: '#1976d2',
      backgroundColor: '#1976d2',
    },
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: 'white',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    '&.selected': {
      opacity: 1,
    },
  },
  radioTitle: {
    fontSize: '1rem',
    fontWeight: 500,
    color: '#374151',
    marginBottom: theme.spacing(0.5),
  },
  radioDescription: {
    fontSize: '0.875rem',
    color: '#6b7280',
    marginLeft: 35,
  },
}));

const AddInstantSurveyModal = ({
                                 open,
                                 onClose,
                                 setFieldValue,
                                 values,
                                 setSubmitting,
                                 editMode = false,
                                 surveyToEdit = null,
                                 editAccount,
                               }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { en: { btn, titles } } = useSelector((state) => state.i18n);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Get assessment instruments from Redux store
  const {
    assessmentInstruments,
  } = useSelector((state) => state.instantAssessment);

  // Local form state - initialize with edit values if in edit mode
  const [formData, setFormData] = useState({
    title: editMode && surveyToEdit ? surveyToEdit.title : '',
    surveyType: editMode && surveyToEdit ? surveyToEdit.surveyType : '',
    durationType: 'never',
    expirationDate: null,
    maxSubmissions: editMode && surveyToEdit ? surveyToEdit.maxSubmissions : '',
    isAnonymous: editMode && surveyToEdit ? surveyToEdit.isAnonymous : true,
  });

  // Reset form when modal opens with different survey
  useEffect(() => {
    if (open && editMode && surveyToEdit) {
      setFormData({
        title: surveyToEdit.title || '',
        surveyType: surveyToEdit.surveyType || '',
        durationType: 'never',
        expirationDate: null,
        maxSubmissions: surveyToEdit.maxSubmissions || '',
        isAnonymous: surveyToEdit.isAnonymous ?? true,
      });
    } else if (open && !editMode) {
      // Reset form for create mode
      setFormData({
        title: '',
        surveyType: '',
        durationType: 'never',
        expirationDate: null,
        maxSubmissions: '',
        isAnonymous: true,
      });
    }

    // Clear validation errors when modal opens
    if (open) {
      setValidationErrors({});
    }
  }, [open, editMode, surveyToEdit]);

  const handleFormDataChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Old validity unit options kept commented for reference
  // const validityUnitOptions = [
  //   { label: 'Hours', value: 'Hours' },
  //   { label: 'Days', value: 'Days' },
  //   { label: 'Weeks', value: 'Weeks' },
  //   { label: 'Months', value: 'Months' },
  // ];

  // Helper to parse backend validation errors
  const parseValidationErrors = (backendErrors) => {
    const errors = {};
    if (backendErrors && typeof backendErrors === 'object') {
      Object.entries(backendErrors).forEach(([path, message]) => {
        // Handle paths like "instantSurveys[0].title" -> "title"
        // or "instantSurveys[0].maxSubmissions" -> "maxSubmissions"
        const fieldMatch = path.match(/\.([^.\[\]]+)$/);
        if (fieldMatch) {
          const fieldName = fieldMatch[1];
          errors[fieldName] = message;
        }
      });
    }
    return errors;
  };

  const handleSaveSurvey = async () => {
    setIsSubmitting(true);
    setValidationErrors({}); // Clear previous errors
    let _values;
    let updatedSurveys;

    try {
      if (editMode) {
        // Update existing survey
        const updatedSurvey = {
          ...surveyToEdit,
          title: formData.title,
          surveyType: formData.surveyType,
          isAnonymous: formData.isAnonymous,
          maxSubmissions: formData.maxSubmissions || null,
        };

        // Update in the parent form's instantSurveys array
        const currentSurveys = values.instantSurveys || [];
        updatedSurveys = currentSurveys.map(survey =>
          survey.key === surveyToEdit.key ? updatedSurvey : survey,
        );

        _values = { ...values, instantSurveys: updatedSurveys };
      } else {
        // Create new survey
        const surveyKey = `instant-survey-${Date.now()}-${Math.random().toString(36).substring(7)}`;

        const newSurvey = {
          key: surveyKey,
          title: formData.title,
          surveyType: formData.surveyType,
          isAnonymous: formData.isAnonymous,
          maxSubmissions: formData.maxSubmissions || null,
          status: 'draft', // New surveys start as draft
        };

        // Add to the parent form's instantSurveys array
        const currentSurveys = values.instantSurveys || [];
        updatedSurveys = [newSurvey, ...currentSurveys];

        _values = { ...values, instantSurveys: updatedSurveys };
      }

      // Trigger main form submit
      if (editAccount) {
        // Store a reference to handle the submission result
        window.currentModalSubmitHandler = {
          onSuccess: () => {
            onClose();
            setIsSubmitting(false);
            setFieldValue('instantSurveys', updatedSurveys);
          },
          onError: (error) => {
            if (error && error.validationErrors) {
              const fieldErrors = parseValidationErrors(error.validationErrors);
              setValidationErrors(fieldErrors);
              dispatch(enqueueSnackbar({
                message: 'Please fix the validation errors below',
                options: { variant: SNACKBAR_VARIANTS.error },
              }));
            } else {
              dispatch(enqueueSnackbar({
                message: error.message || `Failed to ${editMode ? 'update' : 'create'} instant assessment`,
                options: { variant: SNACKBAR_VARIANTS.error },
              }));
            }
            setIsSubmitting(false);
          },
        };
        editAccount(_values, { setSubmitting });
      } else {
        // If no handleSubmit provided, just close
        onClose();
        setIsSubmitting(false);
      }

    } catch (error) {
      console.error('Error saving survey:', error);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={editMode ? 'Edit Instant Assessment' : 'Create New Instant Assessment'}
      open={open}
      onClose={onClose}
      paperClass={classes.modal}
      closeButton
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      }}>
        <div className={classes.scrollableContent}>
          {/* Basic Information Section */}
          <div className={`${classes.section} ${editMode ? classes.sectionDisabled : ''}`} style={{
            cursor: editMode ? 'not-allowed' : 'default'
          }}>
            <div className={classes.sectionTitle}>Basic Information</div>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: editMode ? 'rgba(0, 0, 0, 0.38)' : '#666',
                  marginBottom: 8,
                }}>
                  Assessment Type
                </Typography>
                <div style={{
                  cursor: editMode ? 'not-allowed' : 'default'
                }}>
                  <AssessmentSelector
                    selectedInstrument={formData.surveyType}
                    onInstrumentChange={(instrument) => {
                      const value = instrument?.id;
                      handleFormDataChange('surveyType', value);
                      setFormData(prev => ({ ...prev, 'title': instrument?.name || '' }));
                    }}
                    assessmentInstruments={assessmentInstruments}
                    hideAllOption={true}
                    disabled={editMode}
                  />
                  {validationErrors.surveyType && (
                    <div style={{
                      color: '#d32f2f',
                      fontSize: '0.75rem',
                      marginTop: 4,
                      marginLeft: 14,
                      marginRight: 14,
                    }}>
                      {validationErrors.surveyType}
                    </div>
                  )}
                </div>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Instant Assessment Name"
                  placeholder="Enter a descriptive name for this instant assessment"
                  fullWidth
                  variant="outlined"
                  value={formData.title}
                  onChange={(e) => handleFormDataChange('title', e.target.value)}
                  error={!!validationErrors.title}
                  helperText={validationErrors.title}
                  size="small"
                  disabled={editMode}
                  InputProps={{
                    style: {
                      height: 44,
                      fontSize: 14,
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      fontSize: 14,
                      paddingTop: 2,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </div>

          {/* Privacy Settings Section */}
          <div className={`${classes.section} ${editMode ? classes.sectionDisabled : ''}`} style={{
            cursor: editMode ? 'not-allowed' : 'default'
          }}>
            <div className={classes.sectionTitle}>Privacy Settings</div>
            <FormControlLabel
              control={
                <ElegantSwitch
                  checked={formData.isAnonymous}
                  onChange={(e) => handleFormDataChange('isAnonymous', e.target.checked)}
                  disabled={editMode}
                />
              }
              label="Anonymous Mode (No patient information collected)"
              className={classes.switchControl}
            />
            {!formData.isAnonymous && (
              <div className={classes.infoBox}>
                Patient information (name, email, phone) will be collected within the survey
              </div>
            )}
          </div>

          {/* Duration Settings Section */}
          {/*<div className={classes.section}>*/}
          {/*  <div className={classes.sectionTitle}>Duration Settings</div>*/}

          {/*  <div className={classes.radioGroup}>*/}
          {/*    <div*/}
          {/*      className={`${classes.radioOption} ${formData.durationType === 'never' ? 'selected' : ''}`}*/}
          {/*      onClick={() => handleFormDataChange('durationType', 'never')}*/}
          {/*    >*/}
          {/*      <div className={classes.radioHeader}>*/}
          {/*        <div className={`${classes.radioCircle} ${formData.durationType === 'never' ? 'selected' : ''}`}>*/}
          {/*          <div className={`${classes.radioDot} ${formData.durationType === 'never' ? 'selected' : ''}`} />*/}
          {/*        </div>*/}
          {/*        <div className={classes.radioTitle}>Never Expires</div>*/}
          {/*      </div>*/}
          {/*      <div className={classes.radioDescription}>*/}
          {/*        This assessment will remain active indefinitely*/}
          {/*      </div>*/}
          {/*    </div>*/}

          {/*    <div*/}
          {/*      className={`${classes.radioOption} ${formData.durationType === 'limited' ? 'selected' : ''}`}*/}
          {/*      onClick={() => handleFormDataChange('durationType', 'limited')}*/}
          {/*    >*/}
          {/*      <div className={classes.radioHeader}>*/}
          {/*        <div className={`${classes.radioCircle} ${formData.durationType === 'limited' ? 'selected' : ''}`}>*/}
          {/*          <div className={`${classes.radioDot} ${formData.durationType === 'limited' ? 'selected' : ''}`} />*/}
          {/*        </div>*/}
          {/*        <div className={classes.radioTitle}>Limited Time</div>*/}
          {/*      </div>*/}
          {/*      <div className={classes.radioDescription}>*/}
          {/*        Set a specific expiration period for this assessment*/}
          {/*      </div>*/}

          {/*      {formData.durationType === 'limited' && (*/}
          {/*        <Grid container spacing={2} style={{ marginTop: 16, marginLeft: 0, width: '100%' }}>*/}
          {/*          <Grid item xs={12}>*/}
          {/*            <CalendarPopover*/}
          {/*              shouldRange={false}*/}
          {/*              date={formData.expirationDate}*/}
          {/*              onChange={(newDate) => handleFormDataChange('expirationDate', newDate)}*/}
          {/*              minDate={new Date()}*/}
          {/*              label="Expiration Date"*/}
          {/*              width="100%"*/}
          {/*              shouldReduceFontSize={false}*/}
          {/*              shortView={false}*/}
          {/*              placeholder="Select expiration date"*/}
          {/*              updateAfterChanging*/}
          {/*            />*/}
          {/*          </Grid>*/}
          {/*          /!* Old implementation kept commented*/}
          {/*          <Grid item xs={6}>*/}
          {/*            <FormikTextField*/}
          {/*              name="validityPeriod"*/}
          {/*              label="Validity Period"*/}
          {/*              type="number"*/}
          {/*              fullWidth*/}
          {/*              placeholder="e.g., 7"*/}
          {/*            />*/}
          {/*          </Grid>*/}
          {/*          <Grid item xs={6}>*/}
          {/*            <FormikSelectField*/}
          {/*              name="validityUnit"*/}
          {/*              label="Unit"*/}
          {/*              menuItems={validityUnitOptions}*/}
          {/*              fullWidth*/}
          {/*            />*/}
          {/*          </Grid>*/}
          {/*          *!/*/}
          {/*        </Grid>*/}
          {/*      )}*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*</div>*/}

          {/* Submissions Section */}
          <div className={classes.section}>
            <div className={classes.sectionTitle}>Submissions</div>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Maximum Submissions"
                  placeholder="Leave empty for unlimited submissions"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={formData.maxSubmissions}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow numbers
                    if (value === '' || /^\d+$/.test(value)) {
                      handleFormDataChange('maxSubmissions', value);
                    }
                  }}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  error={!!validationErrors.maxSubmissions}
                  helperText={validationErrors.maxSubmissions}
                  size="small"
                  InputProps={{
                    style: {
                      height: 44,
                      fontSize: 14,
                    },
                    inputProps: {
                      min: 0,
                      pattern: '[0-9]*',
                    },
                  }}
                  InputLabelProps={{
                    style: {
                      fontSize: 14,
                      paddingTop: 2,
                    },
                  }}
                />
                <div className={classes.infoBox}>
                  The assessment will automatically close after reaching this limit
                </div>
              </Grid>
            </Grid>
          </div>
        </div>

        <div className={classes.actionsContainer}>
          <Button
            variant="outlined"
            onClick={onClose}
            className={`${classes.actionButton} ${classes.cancelButton}`}
            disabled={isSubmitting}
          >
            {btn.cancel}
          </Button>
          <Button
            onClick={handleSaveSurvey}
            variant="contained"
            className={`${classes.actionButton} ${classes.createButton}`}
            disabled={isSubmitting}
          >
            {editMode ? btn.save : btn.create}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddInstantSurveyModal;
